import React from "react";
import { SearchX } from "lucide-react";
import { VideoCard } from "./VideoCard.js";
import { VideoItem } from "../types/video.js";

export interface VideoGridProps {
  videos: VideoItem[];
  onVideoSelect: (video: VideoItem) => void;
  isLoading?: boolean;
  onResetFilters?: () => void;
  onEditVideo?: (video: VideoItem) => void;
  onDeleteVideo?: (video: VideoItem) => void;
  onCreatorClick?: (creatorHandleOrName: string, video: VideoItem) => void;
  watchHistoryMap?: Record<string, { lastPosition: number; duration: number }>;
}

export const VideoGrid: React.FC<VideoGridProps> = ({
  videos,
  onVideoSelect,
  isLoading = false,
  onResetFilters,
  onEditVideo,
  onDeleteVideo,
  onCreatorClick,
  watchHistoryMap,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 font-sans w-full">
        {Array.from({ length: 8 }).map((_, idx) => (
          <div key={`skel_${idx}`} className="flex flex-col space-y-3 animate-pulse select-none">
            {/* 16:9 Thumbnail Skeleton */}
            <div className="relative aspect-video w-full rounded-xl sm:rounded-2xl bg-zinc-900/90 border border-zinc-800/80 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-zinc-800/40 to-transparent -translate-x-full animate-[shimmer_1.5s_infinite]" />
            </div>
            {/* Info Skeleton */}
            <div className="flex items-start gap-3 px-0.5">
              <div className="w-9 h-9 rounded-full bg-zinc-800 shrink-0" />
              <div className="space-y-2 flex-1 pt-0.5">
                <div className="h-3.5 bg-zinc-800 rounded-md w-4/5" />
                <div className="h-2.5 bg-zinc-800/60 rounded-md w-1/2" />
                <div className="h-2.5 bg-zinc-800/40 rounded-md w-1/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-20 space-y-4 font-sans bg-zinc-950/80 border border-zinc-800/80 rounded-3xl p-8 max-w-xl mx-auto shadow-xl">
        <div className="w-16 h-16 rounded-3xl bg-zinc-900 border border-zinc-800 text-zinc-500 flex items-center justify-center mx-auto shadow-inner">
          <SearchX className="w-8 h-8 text-emerald-400/80" />
        </div>
        <div className="space-y-1.5">
          <h3 className="text-base font-bold text-white">No videos matching your filter</h3>
          <p className="text-xs text-zinc-400 max-w-sm mx-auto">
            Try selecting a different category or refining your search keywords.
          </p>
        </div>
        {onResetFilters && (
          <button
            onClick={onResetFilters}
            className="px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 text-emerald-400 hover:text-emerald-300 border border-zinc-800 text-xs font-bold rounded-xl transition-all shadow-sm active:scale-95 cursor-pointer"
          >
            Clear All Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 font-sans w-full">
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          video={video}
          onClick={onVideoSelect}
          onEdit={onEditVideo}
          onDelete={onDeleteVideo}
          onCreatorClick={onCreatorClick}
          watchHistory={watchHistoryMap?.[video.id]}
        />
      ))}
    </div>
  );
};
